import { useCallback, useEffect, useMemo, memo, useState } from 'react'
import { ThemeMode } from '@hotmart/space-lib/dist/styles/themes/enum'
import { useUser } from 'src/hook/useUser'
import { CustomProgressInputRange } from './elapsedTime.styles'
import { ElapsedTimeProps } from './elapseTime.types'

const ElapsedTime = ({ audioRef }: ElapsedTimeProps) => {
  const { theme } = useUser()
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [shouldRenderProgressThumb, setShouldRenderProgressThumb] = useState<boolean>(false)

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return

    const newTime = parseFloat(e.target.value)
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const loadedMetadata = () => setDuration(audio.duration || 0)
    const timeUpdate = () => setCurrentTime(audio.currentTime)

    audio.addEventListener('loadedmetadata', loadedMetadata)
    audio.addEventListener('timeupdate', timeUpdate)

    return () => {
      audio.removeEventListener('loadedmetadata', loadedMetadata)
      audio.removeEventListener('timeupdate', timeUpdate)
    }
  }, [])

  const formatTime = useCallback((time: number): string => {
    if (isNaN(time)) return '00:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0')
    return `${minutes}:${seconds}`
  }, [])

  const formattedCurrentTime = formatTime(currentTime)
  const formattedDurationTime = useMemo(() => formatTime(duration), [duration])

  return (
    <>
      <span className="_ml-4 _text-1">{`${formattedCurrentTime} / ${formattedDurationTime}`}</span>

      <CustomProgressInputRange
        $isDarkMode={theme === ThemeMode.DARK}
        onMouseEnter={() => setShouldRenderProgressThumb(true)}
        onMouseLeave={() => setShouldRenderProgressThumb(false)}
        shouldRenderThumb={shouldRenderProgressThumb}
        type="range"
        min={0}
        max={duration}
        step={0.01}
        value={currentTime}
        valueProgress={currentTime}
        onChange={handleProgressChange}
        className="_ml-4 _h-1 _flex-1 _d-flex"
      />
    </>
  )
}

export default memo(ElapsedTime)
